/**
 * standards.ts - Normy i wymagania dla różnych zastosowań
 * PlexiSystem Engineering Module
 */

export type UseCase = 
  | 'machine_guard' 
  | 'food_contact' 
  | 'pos_display' 
  | 'uv_heat_resistance'
  | 'medical'
  | 'cleanroom'
  | 'explosion_proof';

export interface StandardRequirements {
  refs: string[];                    // Lista norm
  minima?: Record<string, string>;   // Minimalne wymagania
  tests?: string[];                  // Wymagane testy
  certificates?: string[];           // Potrzebne certyfikaty
}

export const STANDARDS: Record<UseCase, StandardRequirements> = {
  machine_guard: {
    refs: [
      "PN-EN ISO 14120:2016",  // Osłony - wymagania ogólne
      "PN-EN ISO 13857:2020",  // Bezpieczne odległości
      "PN-EN 60204-1:2018",    // Bezpieczeństwo maszyn - wyposażenie elektryczne
      "PN-EN ISO 12100:2012"   // Bezpieczeństwo maszyn - zasady ogólne
    ],
    minima: {
      edge_radius: "≥2 mm",
      thickness_PC: "4-8 mm",
      thickness_PMMA: "5-10 mm",
      thickness_PETG: "6-12 mm",
      impact_PC: ">30 kJ/m²",
      impact_PMMA: ">2 kJ/m²",
      mounting: "Mocowanie uniemożliwiające demontaż bez narzędzi"
    },
    tests: [
      "Test udarności wg PN-EN ISO 179-1",
      "Test wytrzymałości na rozciąganie",
      "Test stabilności wymiarowej"
    ],
    certificates: [
      "Deklaracja zgodności CE",
      "Certyfikat materiału",
      "Protokół z testów udarności"
    ]
  },
  
  food_contact: {
    refs: [
      "Rozporządzenie WE 1935/2004",
      "Rozporządzenie WE 10/2011",
      "PN-EN 1186:2003",
      "PN-EN ISO 22000:2018"
    ],
    minima: {
      wall_thickness: "≥2 mm",
      surface_roughness: "Ra ≤0.8 μm",
      edge_radius: "≥2 mm",
      cleaning: "Łatwe do czyszczenia, bez martwych stref",
      materials: "Tylko materiały z atestem PZH/FDA"
    },
    tests: [
      "Test migracji globalnej",
      "Test migracji specyficznej",
      "Test organoleptyczny"
    ],
    certificates: [
      "Atest PZH",
      "Certyfikat FDA (dla USA)",
      "Deklaracja zgodności z 1935/2004"
    ]
  },
  
  pos_display: {
    refs: [
      "PN-EN ISO 179-1:2010",   // Tworzywa - udarność
      "PN-EN ISO 4892-2:2013",  // Starzenie UV
      "PN-EN ISO 306:2014"      // Temperatura mięknienia
    ],
    minima: {
      edges: "Promień ≥2 mm lub fazowane",
      stability: "Stabilność przy kącie 10°",
      uv_resistance: "Kategoria UV min. 3",
      flame_retardancy: "Opcjonalnie HB wg UL94",
      transparency: ">85% dla materiałów przezroczystych"
    },
    tests: [
      "Test stabilności",
      "Test obciążenia półek",
      "Test odporności na zarysowania"
    ]
  },
  
  uv_heat_resistance: {
    refs: [
      "PN-EN ISO 4892-2:2013",  // Metody ekspozycji na UV
      "PN-EN ISO 306:2014",     // Temperatura mięknienia
      "PN-EN ISO 75-2:2013"     // Temperatura ugięcia pod obciążeniem
    ],
    minima: {
      uv_category: "≥4 (2000h bez znaczących zmian)",
      heat_deflection: "HDT ≥65°C przy 1.8 MPa",
      color_stability: "ΔE ≤3 po 1000h UV",
      yellowing_index: "ΔYI ≤5",
      stabilization: "Stabilizatory UV lub powłoka"
    },
    tests: [
      "Test przyspieszonego starzenia UV",
      "Test cyklów termicznych",
      "Test retencji właściwości mechanicznych"
    ]
  },
  
  medical: {
    refs: [
      "PN-EN ISO 10993-1:2021", // Ocena biologiczna wyrobów medycznych
      "PN-EN ISO 13485:2016",   // System zarządzania jakością
      "PN-EN ISO 14971:2020"    // Zarządzanie ryzykiem
    ],
    minima: {
      biocompatibility: "Zgodność z ISO 10993",
      sterilization: "Odporność na sterylizację (ETO/gamma)",
      surface_quality: "Ra ≤0.4 μm",
      transparency: ">88% dla elementów wziernikowych",
      chemical_resistance: "Odporność na środki dezynfekcyjne"
    },
    tests: [
      "Testy cytotoksyczności",
      "Testy na drażnienie",
      "Testy stabilności po sterylizacji"
    ],
    certificates: [
      "Certyfikat biokompatybilności",
      "Certyfikat CE dla wyrobów medycznych",
      "Raport z testów wg ISO 10993"
    ]
  },
  
  cleanroom: {
    refs: [
      "PN-EN ISO 14644-1:2016", // Pomieszczenia czyste
      "VDI 2083",                // Wytyczne dla cleanroom
      "PN-EN 1822:2019"         // Filtry HEPA/ULPA
    ],
    minima: {
      particle_emission: "Klasa ISO 5 lub lepsza",
      outgassing: "<0.1% TML (Total Mass Loss)",
      surface_resistance: "10⁶-10⁹ Ω (ESD safe)",
      cleanability: "Gładka powierzchnia, Ra ≤0.2 μm",
      chemical_compatibility: "Odporność na IPA, H₂O₂"
    },
    tests: [
      "Test emisji cząstek",
      "Test outgassingu",
      "Test właściwości antystatycznych"
    ]
  },
  
  explosion_proof: {
    refs: [
      "PN-EN 60079-0:2018",     // Atmosfery wybuchowe
      "ATEX 2014/34/EU",        // Dyrektywa ATEX
      "PN-EN 13463-1:2009"      // Urządzenia nieelektryczne
    ],
    minima: {
      surface_resistance: "10⁴-10⁹ Ω",
      impact_resistance: "Bez iskrzenia przy uderzeniu",
      temperature_class: "T4 (135°C) lub wyższa",
      marking: "Oznaczenie Ex",
      grounding: "Punkt uziemienia"
    },
    tests: [
      "Test rezystywności powierzchniowej",
      "Test iskrzenia",
      "Test akumulacji ładunku"
    ],
    certificates: [
      "Certyfikat ATEX",
      "Deklaracja zgodności Ex",
      "Protokół pomiarów ESD"
    ]
  }
};

/**
 * Sprawdza zgodność z normami dla danego zastosowania
 */
export function checkCompliance(
  useCase: UseCase,
  parameters: {
    material?: string;
    thickness_mm?: number;
    edge_radius_mm?: number;
    uv_stabilized?: boolean;
    certificates?: string[];
  }
): {
  compliant: boolean;
  missing: string[];
  warnings: string[];
} {
  const standard = STANDARDS[useCase];
  const missing: string[] = [];
  const warnings: string[] = [];
  let compliant = true;
  
  // Sprawdź minimalne wymagania
  if (standard.minima) {
    // Sprawdzenie grubości
    if (parameters.thickness_mm && parameters.material) {
      const thicknessKey = `thickness_${parameters.material}`;
      if (standard.minima[thicknessKey]) {
        const range = standard.minima[thicknessKey].match(/(\d+)-(\d+)/);
        if (range) {
          const [, min, max] = range;
          if (parameters.thickness_mm < parseInt(min) || parameters.thickness_mm > parseInt(max)) {
            warnings.push(`Grubość ${parameters.thickness_mm}mm poza zalecanym zakresem ${standard.minima[thicknessKey]}`);
          }
        }
      }
    }
    
    // Sprawdzenie promienia krawędzi
    if (parameters.edge_radius_mm !== undefined && standard.minima.edge_radius) {
      const minRadius = parseInt(standard.minima.edge_radius.match(/≥(\d+)/)?.[1] || '0');
      if (parameters.edge_radius_mm < minRadius) {
        missing.push(`Promień krawędzi <${minRadius}mm`);
        compliant = false;
      }
    }
    
    // Sprawdzenie stabilizacji UV
    if (useCase === 'uv_heat_resistance' && !parameters.uv_stabilized) {
      missing.push('Brak stabilizacji UV');
      compliant = false;
    }
  }
  
  // Sprawdź certyfikaty
  if (standard.certificates) {
    const requiredCerts = standard.certificates;
    const haveCerts = parameters.certificates || [];
    
    for (const cert of requiredCerts) {
      if (!haveCerts.some(c => c.includes(cert))) {
        warnings.push(`Brak certyfikatu: ${cert}`);
      }
    }
  }
  
  return {
    compliant,
    missing,
    warnings
  };
}

/**
 * Generuje listę wymaganych testów
 */
export function getRequiredTests(useCase: UseCase): string[] {
  return STANDARDS[useCase].tests || [];
}

/**
 * Generuje szablon deklaracji zgodności
 */
export function generateComplianceDeclaration(
  useCase: UseCase,
  product: {
    name: string;
    material: string;
    thickness_mm: number;
    manufacturer: string;
    date: Date;
  }
): string {
  const standard = STANDARDS[useCase];
  const date = product.date.toLocaleDateString('pl-PL');
  
  return `
DEKLARACJA ZGODNOŚCI

Producent: ${product.manufacturer}
Data: ${date}

Niniejszym deklarujemy, że wyrób:
- Nazwa: ${product.name}
- Materiał: ${product.material}
- Grubość: ${product.thickness_mm} mm

Jest zgodny z następującymi normami:
${standard.refs.map(ref => `- ${ref}`).join('\n')}

Zastosowanie: ${useCase.replace(/_/g, ' ')}

${standard.minima ? `
Spełnione wymagania minimalne:
${Object.entries(standard.minima).map(([key, value]) => `- ${key}: ${value}`).join('\n')}
` : ''}

${standard.tests ? `
Wykonane testy:
${standard.tests.map(test => `- ${test}`).join('\n')}
` : ''}

Podpis: _______________________
${product.manufacturer}
  `.trim();
}

/**
 * Sprawdza kompatybilność materiału z use case
 */
export function checkMaterialForUseCase(
  material: string,
  useCase: UseCase
): {
  suitable: boolean;
  reason?: string;
} {
  const compatibility: Record<UseCase, string[]> = {
    machine_guard: ['PC', 'PMMA', 'PETG'],
    food_contact: ['PMMA', 'PETG', 'PC'], // Z atestem
    pos_display: ['PMMA', 'PETG', 'PC', 'HIPS', 'ABS'],
    uv_heat_resistance: ['PMMA', 'PC'], // Ze stabilizacją
    medical: ['PC', 'PMMA', 'PETG'],
    cleanroom: ['PC', 'PMMA', 'PVC'],
    explosion_proof: ['PC', 'PMMA'] // Z powłoką antystatyczną
  };
  
  const suitable = compatibility[useCase]?.includes(material) || false;
  
  if (!suitable) {
    return {
      suitable: false,
      reason: `Materiał ${material} nie jest zalecany dla zastosowania: ${useCase}`
    };
  }
  
  // Dodatkowe ostrzeżenia
  if (useCase === 'food_contact') {
    return {
      suitable: true,
      reason: 'Wymagany atest PZH/FDA dla materiału'
    };
  }
  
  if (useCase === 'explosion_proof') {
    return {
      suitable: true,
      reason: 'Wymagana powłoka antystatyczna'
    };
  }
  
  return { suitable: true };
}
